import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import Modal from 'chameleon/packages/blue-book-player/library/modal';
import DragButton from 'chameleon/packages/blue-book-player/library/modal/DraggableModal/DragButton';
import ResizeButton from 'chameleon/packages/blue-book-player/library/modal/DraggableModal/ResizeButton';
import {
  resetToDefaultPosition,
  calculateBounds,
  getCalculateWidth,
  manualCalculatePos,
} from 'chameleon/packages/blue-book-player/InnerContainer/utils';
import { KEY_CODES } from 'chameleon/packages/blue-book-player/constants/keyCodes';

const DraggableModalWrapper = forwardRef((props, ref) => {
  const {
    title,
    onClose,
    children,
    defaultPosition,
    defaultWidth,
    contentWithSpacing,
    contentClassName,
    isScrollableContent,
    size,
    wrapperClassName,
    dialogClassName,
    draggableClassName,
    wrapperStyles,
    expandedWidth,
    collapsedWidth,
    onToggleSize: onToggleSizeCallback,
    defaultResetTo,
    accessibilityDragButton,
    onDragStart,
    accessibilityCloseButton,
    accessibilityResizeButton,
    focuslockDisabled,
  } = props;
  const isDragging = useRef(false);
  const playerEl = useGetPlayerEl();
  const nodeRef = useRef();
  const dragButtonRef = useRef();
  const [keyboardNavigationEnabled, setKeyboardNavigationEnabled] =
    useState(false);
  const [position, setPosition] = useState(
    defaultPosition || {
      x: 0,
      y: 0,
    },
  );
  const [width, setWidth] = useState(defaultWidth || expandedWidth);
  const [bounds, setBounds] = useState({
    top: 0,
    ...calculateBounds(width),
  });

  const onDragButtonKeyUp = (e) => {
    if (
      [KEY_CODES.return, KEY_CODES.space].includes(e.keyCode) ||
      e.type === 'click'
    ) {
      setKeyboardNavigationEnabled((isEnabled) => {
        const newIsEnabled = !isEnabled;

        if (newIsEnabled) {
          dragButtonRef?.current?.setIsPressed(true);
        }

        return newIsEnabled;
      });
    } else if (e.keyCode === KEY_CODES.tab) {
      setKeyboardNavigationEnabled(false);
    }
  };

  const onDragButtonBlur = () => {
    setKeyboardNavigationEnabled(false);
    //stop the blur event to call setIsPressed, somebody else is dragging and setting the pressed state
    if (!isDragging.current) {
      dragButtonRef?.current?.setIsPressed(false);
    }
  };

  const dragButton = (
    <DragButton
      ref={dragButtonRef}
      onKeyUp={onDragButtonKeyUp}
      onBlur={onDragButtonBlur}
      accessibility={accessibilityDragButton}
    />
  );

  const onToggleSize = (isExpanded) => {
    const newWidth = isExpanded ? expandedWidth : collapsedWidth;
    setWidth(newWidth);
    resetToDefaultPosition(playerEl, newWidth, defaultResetTo, (pos) => {
      setPosition(pos);
    });
    setBounds((currentBounds) => ({
      ...currentBounds,
      ...calculateBounds(newWidth),
    }));
    onToggleSizeCallback?.(isExpanded, newWidth);
  };

  const resizeCta = (
    <ResizeButton
      expandedWidth={expandedWidth}
      defaultWidth={defaultWidth}
      onToggleSize={onToggleSize}
      accessibilityResizeButton={accessibilityResizeButton}
    />
  );

  useEffect(() => {
    if (!defaultPosition) {
      resetToDefaultPosition(playerEl, expandedWidth, defaultResetTo, (pos) => {
        setPosition(pos);
      });
    }
  }, [playerEl, defaultPosition]);

  useEffect(() => {
    const onResize = () => {
      const newWidth = getCalculateWidth(width);
      setWidth(newWidth);
      resetToDefaultPosition(playerEl, newWidth, defaultResetTo, (pos) => {
        setPosition(pos);
      });
      setBounds((currentBounds) => ({
        ...currentBounds,
        ...calculateBounds(newWidth),
      }));
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [width]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const { keyCode } = e;
      if (
        [
          KEY_CODES.upArrow,
          KEY_CODES.downArrow,
          KEY_CODES.leftArrow,
          KEY_CODES.rightArrow,
        ].includes(keyCode)
      ) {
        setPosition((currentPos) => ({
          ...currentPos,
          ...manualCalculatePos(keyCode, currentPos, bounds),
        }));
      }
    };
    if (keyboardNavigationEnabled) {
      document.addEventListener('keydown', onKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [keyboardNavigationEnabled, bounds]);

  useImperativeHandle(ref, () => ({
    getPosition: () => position,
    getWidth: () => width,
  }));

  const onDrag = useCallback((e, data) => {
    const { x, y } = data;
    setPosition({
      x,
      y,
    });
  }, []);

  const onStart = useCallback(() => {
    isDragging.current = true;
    dragButtonRef?.current?.setIsPressed(true);
    onDragStart?.();
  }, [dragButtonRef]);

  const onStop = useCallback(() => {
    isDragging.current = false;
    dragButtonRef?.current?.setIsPressed(false);
  }, [dragButtonRef]);

  useLayoutEffect(() => {
    setPosition(defaultPosition);
  }, [defaultPosition]);

  return (
    <Draggable
      defaultClassName={draggableClassName}
      enableUserSelectHack
      position={position}
      onStart={onStart}
      onDrag={onDrag}
      onStop={onStop}
      offsetParent={document.body}
      handle=".draggable"
      bounds={bounds}
      nodeRef={nodeRef}
      cancel=".resize-button, .close-button"
    >
      <div ref={nodeRef} className="absolute top-0 left-0">
        <Modal
          suppressEscapeKeyBehavior
          isCloseButtonInverted
          isDraggable
          contentWithSpacing={contentWithSpacing}
          isScrollableContent={isScrollableContent}
          title={title}
          topBar={dragButton}
          additionalCtas={resizeCta}
          topBarClassName="draggable min-h-[42px] h-[42px] bg-bluebook-gray-900 text-bluebook-white1 pr-[14px] pl-[12px] cursor-move has-[button[aria-pressed='true']]:cursor-grabbing"
          wrapperClassName={`${wrapperClassName} h-[624px] shadow-[0_0_50px_rgba(0,0,0,.2)] border border-bluebook-black`}
          wrapperStyles={wrapperStyles}
          contentClassName={contentClassName}
          dialogClassName={dialogClassName}
          size={size ? size : width === expandedWidth ? 'big' : 'small'}
          onClose={onClose}
          accessibilityCloseButton={accessibilityCloseButton}
          focuslockDisabled={focuslockDisabled}
        >
          {children}
        </Modal>
      </div>
    </Draggable>
  );
});

DraggableModalWrapper.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onClose: PropTypes.func.isRequired,
  defaultPosition: PropTypes.object,
  defaultWidth: PropTypes.string,
  contentWithSpacing: PropTypes.bool,
  contentClassName: PropTypes.string,
  isScrollableContent: PropTypes.bool,
  size: PropTypes.string,
  wrapperClassName: PropTypes.string,
  dialogClassName: PropTypes.string,
  draggableClassName: PropTypes.string,
  wrapperStyles: PropTypes.object,
  expandedWidth: PropTypes.string.isRequired,
  collapsedWidth: PropTypes.string.isRequired,
  defaultResetTo: PropTypes.object,
  accessibilityDragButton: PropTypes.object,
  accessibilityResizeButton: PropTypes.func,
  accessibilityCloseButton: PropTypes.object,
  onDragStart: PropTypes.func,
  focuslockDisabled: PropTypes.bool,
};

DraggableModalWrapper.defaultProps = {
  title: null,
  defaultPosition: null,
  defaultWidth: null,
  contentWithSpacing: true,
  contentClassName: '',
  isScrollableContent: false,
  size: null,
  wrapperClassName: null,
  dialogClassName: null,
  draggableClassName: 'z-overModals',
  wrapperStyles: {},
  accessibilityDragButton: {},
  accessibilityResizeButton: () => {},
  accessibilityCloseButton: {},
  onDragStart: () => {},
  focuslockDisabled: false,
};

export default DraggableModalWrapper;
